<template>
  <div class="details">
    <!-- Header met close button -->
    <div class="header-container">
      <div v-if="getField('header')" class="header">
        {{ getField('header')?.prefix || '' }}{{ resolveDisplayValue(getField('header'), item?.[getField('header')?.key || '']) }}
      </div>
      <button class="close-btn" @click="$emit('close')">
        <i class="fas fa-times"></i>
      </button>
    </div>
    
    <!-- Subheader -->
    <div v-if="getField('subheader')" class="subheader">
      {{ resolveDisplayValue(getField('subheader'), item?.[getField('subheader')?.key || '']) }}
    </div>
    
    <!-- Image -->
    <div v-if="getField('image') && item?.[getField('image')?.key || '']" class="image">
      <img :src="item?.[getField('image')?.key || '']" :alt="getField('image')?.key">
    </div>
    
    <!-- Text fields -->
    <template v-for="field in textFields" :key="field.key">
      <div class="text" v-if="item?.[field.key]">
        <strong>{{ field.defaultText || field.key }}:</strong> 
        {{ resolveDisplayValue(field, item?.[field.key]) }}
      </div>
    </template>
    
    <!-- Features -->
    <div v-if="item?.features?.items?.length" class="features">
      <div v-for="feature in item.features.items" :key="feature.key">
        <i 
          class="fas" 
          :class="getIconClass(feature.icon)"
          :style="{ color: feature.color }"
          :title="feature.key"
        ></i>
        <span>{{ feature.prefix || '' }}{{ feature.value }}{{ feature.suffix || '' }}</span>
      </div>
    </div>
    
    <!-- Links -->
    <template v-for="field in getFieldsByType('link')" :key="field.key">
      <div class="link" v-if="isValidUrl(item?.[field.key])">
        <a 
          :href="item?.[field.key]" 
          :target="field.target || '_blank'"
        >
          {{ field.defaultText || item?.[field.key] }}
        </a>
      </div>
    </template>
    
    <!-- Feedback Form -->
    <FeedbackForm
      v-if="feedbackField && item?.formId && item?.id"
      :formId="item.formId"
      :recordId="item.id"
      :targetField="feedbackField.key"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, inject, Ref, ref, computed } from "vue";
import type { PropType } from "vue";
import FeedbackForm from "./FeedbackForm.vue";

interface Feature {
  key: string;
  icon: string;
  color?: string;
  value: string | number;
  prefix?: string;
  suffix?: string;
}

interface Features {
  items: Feature[];
}

interface MarkerItem {
  type: string;
  features?: Features;
  [key: string]: any;
}

interface MapConfig {
  markerTemplate: {
    fields: Array<{
      type: string;
      key: string;
      prefix?: string;
      defaultText?: string;
      target?: string;
    }>;
  };
}

export default defineComponent({
  name: "MarkerTemplate",
  
  components: {
    FeedbackForm,
  },

  props: {
    item: {
      type: Object as PropType<MarkerItem>,
      required: true,
    },
  },

  setup(props) {
    const mapConfig = inject<Ref<MapConfig>>('mapConfig', ref({
      markerTemplate: { fields: [] }
    }));

    const getIconClass = (icon?: string) => {
      return icon?.startsWith('fa-') ? icon : `fa-${icon || 'question'}`;
    };

    const getField = (type: string) => {
      return mapConfig.value?.markerTemplate?.fields?.find(f => f.type === type);
    };

    const getFieldsByType = (type: string) => {
      return mapConfig.value?.markerTemplate?.fields?.filter(f => f.type === type) || [];
    };

    const isValidUrl = (url?: string) => {
      if (!url) return false;
      try {
        new URL(url);
        return true;
      } catch {
        return false;
      }
    };

    const resolveDisplayValue = (field: any, value: any): string => {
      if (value === undefined || value === null || value === '') return '';
      if (field?.valueLabels) {
        if (Array.isArray(value)) {
          return value.map((v: any) => field.valueLabels[v] ?? v).join(', ');
        }
        return field.valueLabels[value] ?? String(value);
      }
      return Array.isArray(value) ? value.join(', ') : String(value);
    };

    const textFields = computed(() => getFieldsByType('text'));
    
    // Feedback field (als geconfigureerd)
    const feedbackField = computed(() => {
      return mapConfig.value?.markerTemplate?.fields?.find(f => f.type === 'feedback');
    });

    return {
      textFields,
      feedbackField,
      getIconClass,
      getField,
      getFieldsByType,
      isValidUrl,
      resolveDisplayValue
    };
  },
});
</script>

<style scoped>
.details {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  flex: 1;
  overflow: hidden;
  padding-left: 15px;
}

.header, .subheader, .text, .features, .link, .image {
  text-align: left;
}

.header-container {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
}

.header {
  font-size: 1.2rem !important;
  font-weight: bold !important;
  color: #333;
  overflow-wrap: break-word;
  margin-top: 15px;
  margin-bottom: 5px;
}

.subheader {
  font-weight: normal;
  color: #333;
  font-size: 1.2rem !important;
}

.text {
  font-size: 0.9rem;
  margin: 5px 0;
}

.features {
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin: 5px 0;
  width: auto;
}

.features > div {
  display: flex;
  align-items: center;
  gap: 5px;
  background: #f5f5f5;
  padding: 5px;
  border-radius: 4px;
  font-size: 12px;
  width: fit-content;
  border: 1px solid #dee2e6;
}

.link {
  font-size: 12px;
}

.link a {
  color: var(--link-color, #007bff);
  text-decoration: none;
}

.image img {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
}

.close-btn {
  background-color: transparent;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  margin-top: 3px;
  margin-right: 3px;
  font-size: 0.9rem;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background-color: var(--filter-btn-color);
  border-color: var(--filter-btn-color);
  color: white;
}
</style>
